import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { printersApi } from '../api/printers';
import { toErrorMessage } from '../api/client';
import { useStores } from '../auth/StoreContext';
import { Card, PageHead } from '../components/ui';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { Modal } from '../components/Modal';
import { StoreSelector } from '../components/StoreSelector';
import type { PrinterConfig } from '../types';

export function PrinterConfigPage() {
  const { selectedStoreId } = useStores();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PrinterConfig | null>(null);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    queryKey: ['printers', selectedStoreId],
    enabled: selectedStoreId != null,
    queryFn: () => printersApi.list(selectedStoreId as number),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['printers', selectedStoreId] });

  return (
    <>
      <PageHead
        title="Printer Configuration"
        subtitle="80mm Bluetooth thermal printers per store. The Bluetooth address is paired on the POS device."
        actions={
          <>
            <StoreSelector />
            {selectedStoreId != null && (
              <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Add Printer</button>
            )}
          </>
        }
      />
      <Card>
        {selectedStoreId == null ? (
          <Empty icon="🏬" title="Select a store" />
        ) : q.isLoading ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
        ) : !q.data || q.data.length === 0 ? (
          <Empty icon="🖨️" title="No printers configured" hint="Add a printer for this store." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr><th>Name</th><th>BT Address</th><th>Paper</th><th>Default</th><th></th></tr>
              </thead>
              <tbody>
                {q.data.map((p) => (
                  <tr key={p.id}>
                    <td><strong>{p.printerName}</strong></td>
                    <td>{p.btAddress || '— (pair on device)'}</td>
                    <td>{p.paperWidth}</td>
                    <td>{p.isDefault ? <span className="badge badge-green">Default</span> : '—'}</td>
                    <td className="text-right">
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(p)}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {(creating || editing) && selectedStoreId != null && (
        <PrinterModal
          storeId={selectedStoreId}
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); invalidate(); }}
        />
      )}
    </>
  );
}

function PrinterModal({
  storeId, initial, onClose, onSaved,
}: {
  storeId: number;
  initial: PrinterConfig | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.printerName ?? '');
  const [bt, setBt] = useState(initial?.btAddress ?? '');
  const [paper, setPaper] = useState(initial?.paperWidth ?? '80mm');
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [error, setError] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => {
      const body = { storeId, printerName: name, btAddress: bt || undefined, paperWidth: paper, isDefault };
      return initial ? printersApi.update(initial.id, body) : printersApi.create(body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(toErrorMessage(e)),
  });

  return (
    <Modal open title={initial ? 'Edit Printer' : 'Add Printer'} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={!name || m.isPending} onClick={() => { setError(null); m.mutate(); }}>
          {m.isPending ? 'Saving…' : 'Save'}
        </button>
      </>
    }>
      {error && <div className="form-error">{error}</div>}
      <div className="field">
        <label>Printer Name *</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Counter Printer" />
      </div>
      <div className="field">
        <label>Bluetooth Address</label>
        <input value={bt} onChange={(e) => setBt(e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" />
        <div className="hint">Usually set on the POS device when pairing.</div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>Paper Width</label>
          <select value={paper} onChange={(e) => setPaper(e.target.value)}>
            <option value="80mm">80mm</option>
            <option value="58mm">58mm</option>
          </select>
        </div>
        <div className="field">
          <label>Default Printer</label>
          <select value={isDefault ? 'true' : 'false'} onChange={(e) => setIsDefault(e.target.value === 'true')}>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
      </div>
    </Modal>
  );
}
